import { useAuthStore } from '../store/authStore';
import { removeUndefinedValues } from './commonUtils';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

let isRefreshing = false;

const fetchWithRetry = async ({
  url,
  endpoint,
  method,
  queryParams,
  body,
  bodyString,
  rawBody,
  contentType = 'application/json',
  credentials = 'include',
  retry = 0,
  retryDelay = 0,
  returnRawResponse,
}) => {
  let updatedUrl = url + endpoint;

  if (queryParams) {
    queryParams = removeUndefinedValues(queryParams);

    if (Object.keys(queryParams).length) {
      updatedUrl = `${updatedUrl}?${new URLSearchParams(queryParams)}`;
    }
  }

  // Create fresh headers per request to avoid shared state that can prevent cookies from being attached
  const headersInit = { 'content-type': contentType };
  const isLocalUrl =
    url && (url.includes('localhost') || url.includes('127.0.0.1'));
  if (import.meta.env.VITE_API_KEY && isLocalUrl) {
    headersInit['x-api-key'] = import.meta.env.VITE_API_KEY;
  }
  const headers = new Headers(headersInit);

  const fetchParams = {
    credentials,
    method,
    headers,
    // mode: "cors",
    // cache: "no-store",
  };

  if (rawBody) {
    fetchParams.body = rawBody;
  } else if (body) {
    fetchParams.body = JSON.stringify(body);
  } else if (bodyString) {
    fetchParams.body = bodyString;
  }

  try {
    const res = await fetch(updatedUrl, fetchParams);

    if (!res.ok) {
      if (res.status === 401) {
        const currentRefreshToken = useAuthStore.getState().refresh_token;
        if (!currentRefreshToken) {
          throw new Error('Refresh token is missing');
        }

        if (retry < 1) {
          await refreshToken();
          return fetchWithRetry({
            url,
            endpoint,
            queryParams,
            method,
            body,
            bodyString,
            rawBody,
            contentType,
            credentials,
            retry: retry + 1,
            returnRawResponse,
          });
        }
      }

      if (res.status === 400 && endpoint?.includes('refresh-token')) {
        await logOut();
      }

      let errMessage = await res.text();
      let cause;
      try {
        cause = JSON.parse(errMessage);
        if (cause.message) {
          errMessage = cause.message;
        }
      } catch (error) {
        // Not JSON
      }

      const statusText =
        res.status === 504 ? ' 504 Gateway Timeout. Request was sent.' : '';
      throw new Error(errMessage + statusText, { cause });
    }

    if (returnRawResponse) {
      return res;
    }

    const result = await res.json();
    if (result.errorType) {
      throw new Error(result.errorMessage);
    }

    return result;
  } catch (err) {
    console.error(`Fetch Error (${method} ${endpoint}):`, err);
    throw new Error(err.message || 'Something went wrong', {
      cause: err.cause,
    });
  }
};

const get = async ({
  url = BACKEND_URL,
  endpoint,
  queryParams,
  retry = 0,
  returnRawResponse,
}) => {
  return await fetchWithRetry({
    url,
    endpoint,
    queryParams,
    method: 'GET',
    returnRawResponse,
  });
};

const post = async ({
  url = BACKEND_URL,
  endpoint,
  queryParams,
  body,
  bodyString,
  retry = 0,
  returnRawResponse,
}) => {
  return await fetchWithRetry({
    url,
    endpoint,
    queryParams,
    method: 'POST',
    body,
    bodyString,
    returnRawResponse,
  });
};

const patch = async ({
  url = BACKEND_URL,
  endpoint,
  queryParams,
  body,
  retry = 0,
  returnRawResponse,
}) => {
  return await fetchWithRetry({
    url,
    endpoint,
    queryParams,
    method: 'PATCH',
    body,
    returnRawResponse,
  });
};

const put = async ({
  url = BACKEND_URL,
  endpoint,
  queryParams,
  body,
  rawBody,
  contentType,
  credentials,
  retry = 0,
  returnRawResponse,
}) => {
  return await fetchWithRetry({
    url,
    endpoint,
    queryParams,
    method: 'PUT',
    body,
    rawBody,
    contentType,
    credentials,
    returnRawResponse,
  });
};

const fetchDelete = async ({
  url = BACKEND_URL,
  endpoint,
  body,
  queryParams,
  retry = 0,
}) => {
  return await fetchWithRetry({
    url,
    endpoint,
    body,
    queryParams,
    method: 'DELETE',
  });
};

const logOut = async () => {
  try {
    // Attempt to notify backend, but don't let failure stop local logout
    await fetch(`${BACKEND_URL}/auth/logout`, {
      method: 'GET',
      credentials: 'include',
      mode: 'cors',
      cache: 'no-store',
    });
  } catch (error) {
    console.error('Backend logout failed', error);
  }
  useAuthStore.getState().logOut();
};

const refreshToken = async () => {
  if (isRefreshing) {
    // Wait for existing refresh to complete
    while (isRefreshing) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return;
  }

  const refresh_token = useAuthStore.getState().refresh_token;
  if (!refresh_token) {
    throw new Error('No refresh token available');
  }

  isRefreshing = true;
  try {
    const res = await fetch(`${BACKEND_URL}/auth/refresh-token`, {
      method: 'POST',
      credentials: 'include',
      mode: 'cors',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token }),
    });

    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        await logOut();
      }
      throw new Error('Failed to refresh token');
    }

    const result = await res.json();

    // Update store with new values from refresh response
    useAuthStore.getState().logIn({
      roles: result.roles || [],
      nextRefreshTime: result.nextRefreshTime,
    });

    return result;
  } catch (error) {
    console.error('Refresh token error:', error);
    throw error;
  } finally {
    isRefreshing = false;
  }
};

export {
  fetchWithRetry,
  get,
  put,
  post,
  patch,
  fetchDelete,
  refreshToken,
  logOut,
};
